// src/config/permissions.js

export const SCOPES = {
  // --- DASHBOARD ---
  VIEW_DASHBOARD: "dashboard.view",
  
  // --- CRM ---
  VIEW_CRM: "crm.view",
  MANAGE_CRM: "crm.edit", 
  DELETE_CRM: "crm.delete",

  // --- PROJECTS ---
  VIEW_PROJECTS: "project.view",
  MANAGE_PROJECTS: "project.edit",
  DELETE_PROJECTS: "project.delete",

  // --- TASKS ---
  VIEW_TASKS: "task.view",
  MANAGE_TASKS: "task.edit",
  DELETE_TASKS: "task.delete",

  // --- WORKFORCE & HR (Admin) ---
  VIEW_WORKFORCE: "workforce.view",
  MANAGE_WORKFORCE: "workforce.edit",
  DELETE_WORKFORCE: "workforce.delete",
  VIEW_ATTENDANCE: "attendance.view",
  MANAGE_SHIFTS: "shifts.manage",
  MANAGE_LEAVES: "leaves.manage",

  // --- WORKER SELF-SERVICE (field staff / worker role) ---
  // These give a worker access to only their own data, not all employees
  SELF_ATTENDANCE: "attendance.self",   // Clock in/out + view own attendance
  SELF_PAYSLIP: "payslip.self",         // View own payslips only
  SELF_LEAVE: "leave.self",             // Submit & view own leave requests
  VIEW_SITE: "site.view",               // View assigned project/site updates

  // --- FINANCIALS & ACCOUNTING ---
  VIEW_FINANCE: "financial.view",      
  MANAGE_BUDGET: "financial.create",   
  
  VIEW_ACCOUNTING: "accounting.view",
  MANAGE_INVOICES: "accounting.invoices", 
  MANAGE_BILLS: "accounting.bills",       
  APPROVE_TRANSACTIONS: "accounting.approve", 
  VIEW_REPORTS: "accounting.reports",     

  // --- MATERIALS ---
  VIEW_PURCHASE: "purchase.view",
  VIEW_PICKING: "inventory.picking",
  VIEW_SCRAP: "inventory.scrap",

  // --- STAKEHOLDERS ---
  VIEW_VENDORS: "vendor.view",
  VIEW_EMPLOYEES: "employee.view",

  // --- INVENTORY ---
  VIEW_INVENTORY: "inventory.view",
  VIEW_WAREHOUSE: "warehouse.view",

  // --- ASSETS ---
  VIEW_ASSETS: "asset.view",
  MANAGE_ASSETS: "asset.edit",
  DELETE_ASSETS: "asset.delete",

  // --- DOCUMENTS ---
  VIEW_DOCUMENTS: "document.view",

  // --- COMPLIANCE ---
  VIEW_COMPLIANCE: "compliance.view",
  VIEW_REPORTING: "report.view",

  // --- CUSTOMER PORTAL ---
  VIEW_CUSTOMER_PORTAL: "customer.portal",

  // --- ADMIN ---
  MANAGE_USERS: "user.manage", 
  MANAGE_ROLES: "role.manage",

  VIEW_USERS: "user.view",
  CREATE_USER: "user.create",
  EDIT_USER: "user.edit",
  DELETE_USER: "user.delete",

  VIEW_ROLES: "role.view",
  CREATE_ROLE: "role.create",
  EDIT_ROLE: "role.edit",
  DELETE_ROLE: "role.delete",
};

/**
 * Pre-defined permission sets per role type.
 * Use these when creating roles in the DB or seeding.
 *
 * CUSTOMER / CLIENT role: Clients, project owners, homebuyers.
 *   - customer.portal      → access dedicated customer dashboard overview
 *   - project.view         → view assigned projects & progress milestones
 *   - task.view            → view phase tasks and milestones
 *   - accounting.invoices  → view invoices and payment statuses
 *   - document.view        → view project blueprints, drawings & contracts
 *   - crm.view             → support tickets and communication
 *
 * WORKER role: field staff / site workers — see only their own data.
 *   - dashboard.view       → see their personal dashboard
 *   - attendance.self      → clock in/out, view own attendance
 *   - payslip.self         → view own payslips
 *   - leave.self           → submit & track own leave requests
 *   - site.view            → view assigned project/site updates
 *   - workforce.view       → needed so the HR sidebar section shows
 */
export const ROLE_PERMISSION_PRESETS = {
  SUPER_ADMIN: ["ALL"],
  ADMIN: ["ALL"],

  CUSTOMER: [
    SCOPES.VIEW_DASHBOARD,
    SCOPES.VIEW_CUSTOMER_PORTAL,

    // Site Operations & DPRs
    "dpr.view",
    "dpr.create",
    "dpr.edit",
    "dpr.delete",
    "attendance.view",
    "attendance.mark",
    "attendance.self",
    "indent.create",
    "indent.view",
    "indent.edit",
    "grn.create",
    "grn.view",
    "approvals.view",
    "approvals.action",
    "inspection.view",
    "inspection.create",
    "inspection.edit",

    // Projects: Full Access inside module
    SCOPES.VIEW_PROJECTS,
    SCOPES.MANAGE_PROJECTS,
    SCOPES.DELETE_PROJECTS,
    "project.create",
    "project.manage",

    // Tasks: Full Access inside module
    SCOPES.VIEW_TASKS,
    SCOPES.MANAGE_TASKS,
    SCOPES.DELETE_TASKS,
    "task.create",
    "task.manage",

    // Documents: Full Access inside module
    SCOPES.VIEW_DOCUMENTS,
    "document.create",
    "document.upload",
    "document.edit",
    "document.delete",
    "document.manage",

    // CRM: Full Access inside module
    SCOPES.VIEW_CRM,
    SCOPES.MANAGE_CRM,
    SCOPES.DELETE_CRM,
    "crm.create",
    "crm.manage",

    // Finance & Invoices: Full Access inside module
    SCOPES.VIEW_ACCOUNTING,
    SCOPES.MANAGE_INVOICES,
    SCOPES.MANAGE_BILLS,
    SCOPES.VIEW_FINANCE,
    SCOPES.MANAGE_BUDGET,
    SCOPES.APPROVE_TRANSACTIONS,
    SCOPES.VIEW_REPORTS,
    "accounting.create",

    // Compliance & Safety
    SCOPES.VIEW_COMPLIANCE,
    "compliance.create",
    "compliance.edit",

    // Purchases (Site Requisitions)
    SCOPES.VIEW_PURCHASE,
    "purchase.create",
    "purchase.edit",
  ],

  CLIENT: [
    SCOPES.VIEW_DASHBOARD,
    SCOPES.VIEW_CUSTOMER_PORTAL,
    "dpr.view",
    "dpr.create",
    "dpr.edit",
    "attendance.view",
    "attendance.mark",
    "attendance.self",
    "indent.create",
    "indent.view",
    "grn.create",
    "grn.view",
    "approvals.view",
    "approvals.action",
    "inspection.view",
    "inspection.create",
    SCOPES.VIEW_PROJECTS,
    SCOPES.MANAGE_PROJECTS,
    SCOPES.DELETE_PROJECTS,
    "project.create",
    "project.manage",
    SCOPES.VIEW_TASKS,
    SCOPES.MANAGE_TASKS,
    SCOPES.DELETE_TASKS,
    "task.create",
    SCOPES.VIEW_DOCUMENTS,
    "document.create",
    "document.upload",
    "document.edit",
    "document.delete",
    SCOPES.VIEW_CRM,
    SCOPES.MANAGE_CRM,
    SCOPES.DELETE_CRM,
    SCOPES.VIEW_ACCOUNTING,
    SCOPES.MANAGE_INVOICES,
    SCOPES.MANAGE_BILLS,
    SCOPES.VIEW_FINANCE,
    SCOPES.MANAGE_BUDGET,
    SCOPES.VIEW_COMPLIANCE,
    "compliance.create",
    SCOPES.VIEW_PURCHASE,
    "purchase.create",
  ],

  WORKER: [
    SCOPES.VIEW_DASHBOARD,
    SCOPES.SELF_ATTENDANCE,
    SCOPES.SELF_PAYSLIP,
    SCOPES.SELF_LEAVE,
    SCOPES.VIEW_SITE,
    SCOPES.VIEW_WORKFORCE,
  ],

  MANAGER: [
    SCOPES.VIEW_DASHBOARD,
    SCOPES.VIEW_PROJECTS,
    SCOPES.VIEW_TASKS,
    SCOPES.MANAGE_TASKS,
    SCOPES.VIEW_WORKFORCE,
    SCOPES.VIEW_ATTENDANCE,
    SCOPES.MANAGE_LEAVES,
    SCOPES.VIEW_REPORTING,
    SCOPES.VIEW_DOCUMENTS,
  ],

  PROJECT_MANAGER: [
    SCOPES.VIEW_DASHBOARD,
    SCOPES.VIEW_PROJECTS,
    SCOPES.VIEW_TASKS,
    SCOPES.MANAGE_TASKS,
    SCOPES.VIEW_WORKFORCE,
    SCOPES.VIEW_ATTENDANCE,
    SCOPES.MANAGE_LEAVES,
    SCOPES.VIEW_REPORTING,
    SCOPES.VIEW_DOCUMENTS,
  ],
};